<script setup lang="ts">
import { computed, inject, onMounted, ref, useTemplateRef } from "vue";
import { useRoute } from "vue-router";

import CopyableElement from "@/components/ui/CopyableElement.vue";
import DropdownButton from "@/components/ui/DropdownButton.vue";
import DropdownButtonItem from "@/components/ui/DropdownButtonItem.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import { type ProjectDto } from "@/dto";
import { raiseErrorKey } from "@/injectionKeys";
import DefaultPageLayout from "@/layouts/DefaultPageLayout.vue";
import { RouteNames } from "@/router";
import { useApi } from "@/services/api";
import { detectUserOS } from "@/services/utils";
import { useModalsStore } from "@/stores/modals";
import { useToastsStore } from "@/stores/toasts";
import ProjectsListViewCard from "@/views/projects/ProjectsListViewCard.vue";

const api = useApi();
const route = useRoute();
const modals = useModalsStore();
const toasts = useToastsStore();

const raiseError = inject(raiseErrorKey);

const isLoading = ref(true);
const projects = ref<ProjectDto[]>([]);
const isWindows = detectUserOS().toLowerCase().includes("win");
const selectedShell = ref(isWindows ? "powershell" : "bash");
const cardFirstSectionWidth = ref(-1);
const cards = useTemplateRef<HTMLDivElement>("cards");

const workspaceName = computed(() => route.params.workspaceName as string);
const bashSnippet = computed(
  () => `git clone git@github.com:probabl-ai/skore-demo-project.git
cd skore-demo-project
python3 -m venv venv
source venv/bin/activate
pip install -r requirements.txt
skore-hub-login
python demo.py --tenant="${workspaceName.value}" --name="demo"`
);
const powershellSnippet = computed(
  () => `git clone https://github.com/probabl-ai/skore-demo-project.git
cd .\\skore-demo-project\\
python -m venv venv
.\\venv\\Scripts\\Activate.ps1
pip install -r requirements.txt
skore-hub-login
python demo.py --tenant="${workspaceName.value}" --name="demo"`
);
const snippet = computed(() =>
  selectedShell.value === "powershell" ? powershellSnippet.value : bashSnippet.value
);

async function refreshProjects() {
  isLoading.value = true;
  const results = await api.listProjects(workspaceName.value);
  if (results.length >= 0) {
    projects.value = results;
  } else {
    raiseError?.(404);
  }
  isLoading.value = false;
}

async function deleteProject(projectName: string) {
  if (
    await modals.confirm({
      title: "Delete project",
      message: `Do you really want to delete project ${projectName}?`,
    })
  ) {
    const success = await api.deleteProject(workspaceName.value, projectName);
    if (success) {
      toasts.addToast(`Project ${projectName} deleted`, "success", {
        duration: 5,
      });
    }
  }
  await refreshProjects();
}

onMounted(async () => {
  await refreshProjects();
});
</script>

<template>
  <DefaultPageLayout
    :breadcrumb-items="[
      {
        label: workspaceName,
        to: { name: RouteNames.Projects, params: { workspaceName } },
        icon: 'icon-masonry',
      },
    ]"
  >
    <div class="projects-view">
      <div v-if="isLoading" class="loading">
        <SkoreSpinner />
      </div>
      <div v-else class="wrapper">
        <header>
          <div class="text-2xl-medium">Projects</div>
          <div class="subtitle text-base-regular">Browse the projects you created here.</div>
        </header>
        <div v-if="projects.length > 0" class="projects" ref="cards">
          <ProjectsListViewCard
            v-for="project in projects"
            :key="project.id"
            :project="project"
            :workspace-name="workspaceName"
            :first-section-width="cardFirstSectionWidth"
            @delete="deleteProject(project.name)"
          />
        </div>
        <div v-else class="placeholder">
          <header>
            <div class="icon">
              <i class="icon icon-scan-cube" />
            </div>
            <div class="text-xl-medium">No projects yet</div>
            <div class="subtitle text-base-regular">
              Projects appear here when you create them using the skore library in your code.
              <br />
              Check the <a href="https://docs.skore.probabl.ai/">documentation</a> to get started or
              try our tutorial:
            </div>
          </header>
          <div class="snippet card">
            <div class="help">
              <div>
                Run this in a
                <span class="shell-dropdown">
                  <DropdownButton :label="selectedShell" icon-right="icon-chevron-down">
                    <DropdownButtonItem label="bash" @click="selectedShell = 'bash'" />
                    <DropdownButtonItem label="powershell" @click="selectedShell = 'powershell'" />
                  </DropdownButton>
                </span>
                terminal.
              </div>
            </div>
            <CopyableElement :text-to-copy="snippet" class="lines">
              <div v-for="(line, i) in snippet.split('\n')" :key="i" class="line">
                <span class="prompt">$ </span>{{ line }}
              </div>
            </CopyableElement>
          </div>
          <div class="help text-base-regular">
            <em>
              You'll need to install <a href="https://git-scm.com/downloads">git</a> and
              <a href="https://www.python.org/downloads/">Python</a> (version 3.10, to 3.13).
            </em>
          </div>
        </div>
      </div>
    </div>
  </DefaultPageLayout>
</template>

<style scoped>
.projects-view {
  display: flex;
  min-height: 100%;
  flex-direction: column;
  padding-top: var(--numbers-32);
  gap: var(--numbers-32);

  .loading {
    margin-top: 45dvh;
    text-align: center;
  }

  .wrapper {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: var(--numbers-32);

    header {
      display: flex;
      flex-direction: column;
      gap: var(--numbers-4);

      .subtitle {
        color: var(--content-tertiary);
      }
    }

    .projects {
      display: grid;
      gap: var(--numbers-16);
      grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
    }

    .placeholder {
      display: flex;
      flex: 1;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding-bottom: var(--numbers-32);
      gap: var(--numbers-32);
      text-align: center;

      header {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--numbers-16);

        .subtitle {
          color: var(--content-secondary);
        }
      }

      .icon {
        margin-bottom: var(--numbers-12);

        i {
          padding: var(--numbers-8);
          border-radius: var(--numbers-6);
          background-color: var(--background-tertiary);
          color: var(--content-brand);
          vertical-align: middle;
        }
      }

      .snippet {
        background-color: var(--background-primary);
        text-align: left;

        .help {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          padding: 0 var(--numbers-6);

          .shell-dropdown {
            :deep(.button) {
              background-color: var(--background-primary);
            }
          }
        }

        .lines {
          padding: var(--numbers-12);
          border: solid var(--stroke-width-md) var(--border-tertiary);
          border-radius: var(--numbers-12);
          margin: var(--numbers-6);
          background-color: var(--background-tertiary);
          box-shadow: var(--box-shadow-m);
          color: var(--content-brand);

          .line {
            font-family: GeistMono, monospace;
          }

          .prompt {
            color: var(--content-positive);
            user-select: none;
          }
        }
      }
    }
  }
}
</style>
